"use client";

import { type ComponentProps, useEffect, useRef, useState } from "react";

import { Link, usePathname } from "@/i18n/navigation";

type LinkHref = ComponentProps<typeof Link>["href"];
type NavLink = { href: LinkHref; label: string };

/**
 * The services nav item IS the menu: its first child (the section
 * landing link, e.g. /services) always shows and reads as a plain nav
 * link; the panel chrome and remaining links reveal whenever the menu
 * is open.
 *
 * `open` is the single source of truth, driven by pointer hover, focus,
 * and (on hover-less touch devices) the first tap on the landing link.
 * Clicking any item — or any route change — closes the menu, so the
 * panel doesn't linger after navigation when the cursor is still on it.
 */
export function ServicesMenu({ items }: { items: NavLink[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  // Close on navigation. Resetting state during render when a tracked value
  // changes is React's recommended alternative to a setState-in-effect — it
  // avoids the extra commit + render pass the effect would trigger.
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const landingActive = pathname === items[0]?.href;

  // Only intercept the first tap on hover-less devices; pointer devices
  // already opened the menu via mouseenter, so their click should
  // navigate. If the landing route is already current, never navigate —
  // but still allow the touch tap-to-open path.
  const handleLandingClick = (e: React.MouseEvent) => {
    if (landingActive) {
      e.preventDefault();
      if (!open && window.matchMedia("(hover: none)").matches) setOpen(true);
      return;
    }
    if (!open && window.matchMedia("(hover: none)").matches) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false);
  };

  const [landing, ...rest] = items;

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={handleBlur}
    >
      {/* Reserve the inline width of the always-visible label so the nav
          bar lays out normally; the menu overlays it. */}
      <span aria-hidden className="invisible">
        {landing.label}
      </span>
      <ul
        role="menu"
        data-open={open || undefined}
        className="absolute left-[-17px] top-[-13px] border border-transparent px-4 py-3 transition-colors data-[open]:border-line data-[open]:bg-bg/95 data-[open]:shadow-sm data-[open]:backdrop-blur-md"
      >
        <li>
          <Link
            href={landing.href}
            role="menuitem"
            aria-expanded={open}
            aria-current={landingActive ? "page" : undefined}
            onClick={handleLandingClick}
            className="block whitespace-nowrap text-fg-muted hover:text-fg transition-colors aria-[current=page]:text-fg aria-[current=page]:cursor-default"
          >
            {landing.label}
          </Link>
        </li>
        {rest.map((child) => {
          const childActive = pathname === child.href;
          return (
            <li
              key={child.label}
              data-open={open || undefined}
              className="mt-2.5 hidden data-[open]:block"
            >
              <Link
                href={child.href}
                role="menuitem"
                aria-current={childActive ? "page" : undefined}
                onClick={(e) => {
                  if (childActive) e.preventDefault();
                  setOpen(false);
                }}
                className="block whitespace-nowrap text-fg-muted hover:text-fg transition-colors aria-[current=page]:text-fg aria-[current=page]:cursor-default"
              >
                {child.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
